<template>
  <div
    class="mm-panel"
    :class="{
      'mm-panel--tablet-fit': isTabletFluid,
      'mm-panel--tablet-stacked': isTabletStacked,
      'mm-panel--auto': columnCount === 0,
      'mm-panel--fit': columnCount > 0 && isDesktop,
      'mm-panel--promo-filter': showFilterSearch && columnCount === 0,
      'mm-panel--few-cols': columnCount === 1 || columnCount === 2
    }"
    :style="panelStyle"
  >
    <!-- Tail for 2-column + promo menus using the p2_2_stk preset (1 col × 2 rows).
         Fixed left position — no JS needed to track the active nav item. -->
    <div v-if="showTail" class="mm-panel__tail" />
    <div
      class="mm-panel__inner mm-panel__inner--centered"
      :class="{
        'mm-panel__inner--tablet-fit': isTabletFluid,
        'mm-panel__inner--wrap': columnCount >= 3,
        'mm-panel__inner--promo-filter': showFilterSearch && columnCount === 0
      }"
      :style="innerStyle"
    >
      <!-- Promo pane sits on the right. XD screen 18 shows it on the left, but
           no setting for side exists anywhere in the design — see plan open
           question G4/promo-side. Right is what screen 17 and the walkthrough use. -->
      <div v-if="columnCount > 0" class="mm-panel__columns" :style="columnsStyle">
        <mega-menu-column
          v-for="(column, index) in visibleColumns"
          :key="index"
          :column="column"
          :class="{ 'mm-column--tablet-fit': isTabletFluid }"
          :width="widths.column"
          :fixed-width="!!fixedColumnWidth"
          :indent-step="indentStep"
          :divider-allowed="dividerAllowed(index)"
          :reserve-filter-space="filterColumns.includes(index)"
          :inert="inert"
          @navigate="$emit('navigate', $event)"
        />

        <!-- Pinned to the bottom, centred across the COLUMNS only — not the
             whole panel. Verified three times against the XD: the "Design
             Instructions" diagram (3 cols + promo -> under column 2; 2 cols +
             promo -> between columns 1 and 2), the 3-column example artboard,
             and the 128% preview. The XD note "middle of total panes showing"
             means panes = columns.

             Hidden with no columns: the column block would be zero-width, so a
             centred button would sit on the promo pane's left edge. -->
        <div v-if="showFilterSearch && columnCount > 0" class="mm-panel__filter">
          <button type="button" class="mm-panel__filter-btn" @click="onFilterSearch">
            FILTER SEARCH
          </button>
        </div>
      </div>

      <mega-menu-promo-pane
        v-if="item.promo"
        :promo="item.promo"
        :width="widths.promo"
        :pane-height="effectivePaneHeight"
        :grid="promoGrid"
        :ratio="promoRatio"
        :tile-layout="promoTileLayout"
        :parent-column-count="columnCount"
        :inert="inert"
        @navigate="$emit('navigate', $event)"
      />

      <!-- 0-column menus: no column block exists to anchor the FILTER SEARCH
           button. Render it centred across the whole panel bottom instead so
           the promo pane keeps its own layout. -->
      <div
        v-if="showFilterSearch && columnCount === 0"
        class="mm-panel__filter mm-panel__filter--promo"
      >
        <button type="button" class="mm-panel__filter-btn" @click="onFilterSearch">
          FILTER SEARCH
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MegaMenuColumn from './MegaMenuColumn.vue'
import MegaMenuPromoPane from './MegaMenuPromoPane.vue'
import { resolveLayout, dividerAllowedFor, filterOverlapColumns } from '~/pages/admin/mega-menu/draft'
import { isTabletWidth, sharesRowWithColumns } from '~/helpers/mega-menu-tablet'

// FILTER SEARCH button block that lives beneath the promo pane in 0-column
// menus. Button height (~33px: 10/22 padding + 11px font + 1px border) plus
// its wrapper padding (12 top + 24 bottom) = 69. Add a few px for sub-pixel
// rounding so the panel never spills past 82vh and triggers the grid's
// (otherwise-hidden) scrollbar.
const FILTER_BLOCK_RESERVE = 75

// Tablet (768-1279) only. The panel ends this far above the viewport bottom and
// never gets shorter than the minimum that still shows a heading, rows and a tile.
const TABLET_BOTTOM_GAP = 24
const TABLET_MIN_HEIGHT = 460
// .mm-panel__inner side padding in the tablet media block.
const TABLET_INNER_PAD = 20
// Wrapped tablet menus (3-4 columns on a narrow tablet): the column section never
// grows past this, so a long list scrolls inside its column and the promo pane
// stays within reach. Same height the XD gives the column section.
const TABLET_COLUMN_SECTION_MAX = 580
// The panel's own 1px top and bottom border (box-sizing: border-box).
const PANEL_BORDERS = 2
// Tablet shows three description lines where desktop shows two. The promo pane's
// tile math budgets two, so each tile row needs one more 15px line reserved.
const TABLET_EXTRA_DESCRIPTION_LINE = 15

/**
 * The open mega menu for one nav item.
 *
 * Owns the width matrix, the fixed panel height and the Filter Search
 * placement. Shared by the public header and the admin preview, which is what
 * makes "preview matches production" true by construction rather than by
 * discipline.
 *
 * The matrix is a lookup rather than arithmetic so it matches the XD exactly.
 * It mirrors config/mega-menu.php `layout_matrix`, which covers every column
 * count 0-4 with and without a promo pane.
 */

export default {
  name: 'MegaMenuPanel',

  components: { MegaMenuColumn, MegaMenuPromoPane },

  props: {
    /** A row of the public payload, or the same shape from draft.toPublicShape(). */
    item: {
      type: Object,
      required: true
    },
    /** [columns, rows] for the promo grid, resolved from the layout preset. */
    promoGrid: {
      type: Array,
      default: () => [1, 1]
    },
    /** Tile orientation for the preset: '4:3' or '3:4'. */
    promoRatio: {
      type: String,
      default: '4:3'
    },
    /** 'stacked' or 'inline' tile flow for the preset. */
    promoTileLayout: {
      type: String,
      default: 'stacked'
    },
    menuHeight: {
      type: Number,
      default: 580
    },
    indentStep: {
      type: Number,
      default: 10
    },
    showFilterSearch: {
      type: Boolean,
      default: true
    },
    inert: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      panelTop: 0,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
      windowHeight: typeof window !== 'undefined' ? window.innerHeight : 900
    }
  },

  mounted () {
    window.addEventListener('resize', this.onResize)
    this.measureTop()
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
  },

  computed: {
    visibleColumns () {
      // An empty column (no rows) contributes no visible content but would
      // still reserve its matrix width, pushing the promo pane off-centre.
      // Drop them so the layout matrix and centering treat this menu as if
      // those columns did not exist.
      return (this.item.columns || []).filter(column => (column?.rows?.length || 0) > 0)
    },

    columnCount () {
      return this.visibleColumns.length
    },

    hasPromo () {
      return !!this.item.promo
    },

    /** p2_2_stk: 2 nav columns + promo pane with 1 col × 2 rows (2 tiles stacked). */
    showTail () {
      return this.columnCount === 2 &&
        this.hasPromo &&
        this.promoGrid[0] === 1 &&
        this.promoGrid[1] === 2
    },

    /**
     * Falls back to flexible widths rather than throwing when a combination is
     * missing from the matrix. Every 0-4 column count is covered today; the
     * fallback exists so a header can never fail to render if that changes.
     */
    widths () {
      // Tablet, no promo pane: columns keep their matrix width, shrunk only when
      // the viewport cannot fit them side by side.
      if (this.isTabletFluid && !this.hasPromo) {
        const matrix = resolveLayout(this.columnCount, false)
        const fits = Math.floor((this.windowWidth - 2 * TABLET_INNER_PAD) / this.columnCount)

        return { ...matrix, column: Math.min(matrix.column, fits) }
      }

      return resolveLayout(this.columnCount, this.hasPromo)
    },

    /**
     * Desktop (>=1280) pins the panel at 90vh so content adapts to the
     * viewport, not the designed 580px pane. Tablet/mobile keep the prop
     * default so their media-rule heights (580 for 1-2 col, 90vh-230 for 3+)
     * stay the source of truth.
     *
     * 0-column menus route the height through the promo pane's tile-sizing
     * math, so the FILTER SEARCH block (which lives below the promo in that
     * layout) must be subtracted from the pane budget. Otherwise the grid
     * sizes tiles against the full 82vh and the panel overflows the cap,
     * which would otherwise trigger a scrollbar.
     */
    effectivePaneHeight () {
      if (this.windowWidth >= 1280) {
        const cap = Math.round(this.windowHeight * 0.82)

        if (this.columnCount === 0 && this.showFilterSearch) {
          return cap - FILTER_BLOCK_RESERVE
        }

        return cap
      }

      if (this.isTabletRange && !this.inert) {
        const budget = this.tabletHeight - this.thirdDescriptionLine

        return this.columnCount === 0 && this.showFilterSearch
          ? budget - FILTER_BLOCK_RESERVE
          : budget
      }

      return this.menuHeight
    },

    isTabletRange () {
      return isTabletWidth(this.windowWidth)
    },

    /**
     * Tablet menus whose columns and promo pane fit on one row size and flex like
     * desktop (0-2 columns from 768, 3 from 1024, 4 from 1180). Narrower 3-4
     * column menus drop the promo pane onto its own row.
     */
    isTabletFluid () {
      return this.isTabletRange && sharesRowWithColumns(this.columnCount, this.windowWidth)
    },

    /**
     * Height to hold back from the promo tile math for the third description
     * line: one per tile row, when captions sit under the image. The 6-tile
     * 0-column preset keeps its caption beside the image and clamps to the image
     * height instead, so it needs nothing.
     */
    thirdDescriptionLine () {
      const tiles = this.item.promo?.tiles || []
      const captionBeside = this.columnCount === 0 && this.promoTileLayout === 'inline' && tiles.length === 6

      if (captionBeside || !tiles.some(tile => tile.text)) return 0

      return (Number(this.promoGrid[1]) || 1) * TABLET_EXTRA_DESCRIPTION_LINE
    },

    isTabletStacked () {
      return this.isTabletRange && !this.isTabletFluid
    },

    /** From the panel's top edge down to just above the viewport bottom. */
    tabletHeight () {
      const room = this.windowHeight - this.panelTop - TABLET_BOTTOM_GAP

      return Math.round(Math.min(this.windowHeight * 0.82, Math.max(TABLET_MIN_HEIGHT, room)))
    },

    isDesktop () {
      return this.windowWidth >= 1280
    },

    /** Exact px width per column (400/375/350/300), or null when it should flex. */
    fixedColumnWidth () {
      if (this.isTabletFluid && !this.hasPromo) return this.widths.column

      return this.isDesktop && !this.hasPromo ? this.widths.column : null
    },

    innerStyle () {
      return null
    },

    /**
     * 0-column menus size to their promo pane's natural content, capped at
     * menuHeight — so a small promo shrinks the panel and a tall one hits the
     * cap. Every other column count keeps the fixed panel height.
     *
     * The height cap is HARD: when natural content exceeds it, the panel does
     * NOT grow — the scaler wrapper (see measureScale + contentScale) scales
     * all content down proportionally so everything fits inside the cap.
     */
    panelStyle () {
      if (this.windowWidth >= 1280) {
        return { maxHeight: this.effectivePaneHeight + 'px' }
      }

      if (this.isTabletRange && !this.inert) {
        if (this.isTabletFluid) return { maxHeight: this.tabletHeight + 'px' }

        // Without a promo pane the wrapped panel is just the column section, so
        // it fits its content (the section is capped in columnsStyle).
        return this.hasPromo
          ? { height: this.tabletHeight + 'px' }
          : { maxHeight: this.tabletHeight + 'px' }
      }

      if (this.columnCount === 0) {
        return { maxHeight: this.menuHeight + 'px' }
      }

      return { height: this.menuHeight + 'px' }
    },

    scalerStyle () {
      // transform-origin top center so content shrinks around the vertical
      // axis (panel is a wide dropdown — scaling from top-left visually
      // left-aligns a shrunk pane). Translate the scaler width up by 1/scale
      // so when scale < 1 the layout box is wider than the panel and the
      // visually-scaled content still spans the full panel width centred.
      if (this.contentScale >= 1) return null

      return {
        transform: `scale(${this.contentScale})`,
        transformOrigin: 'top center',
        width: `${100 / this.contentScale}%`,
        marginLeft: `${(100 - 100 / this.contentScale) / 2}%`
      }
    },

    /** Columns that must keep their bottom clear of the FILTER SEARCH button. */
    filterColumns () {
      return this.showFilterSearch ? filterOverlapColumns(this.columnCount) : []
    },

    columnsStyle () {
      if (this.isTabletStacked) {
        // Never taller than the panel can show, so a columns-only panel does not
        // clip its own column section.
        const room = this.tabletHeight - PANEL_BORDERS

        return { maxHeight: (this.hasPromo ? TABLET_COLUMN_SECTION_MAX : Math.min(TABLET_COLUMN_SECTION_MAX, room)) + 'px' }
      }

      // Only the column block is a positioning context, so Filter Search
      // centres across the columns and not across the promo pane too.
      // flex-grow proportional to the matrix width keeps the columns:promo
      // ratio at every viewport — block grows with the window and shrinks
      // with it in the same proportion.
      if (!this.widths.column) return null
      const w = this.columnCount * this.widths.column

      // Desktop, no promo pane: columns hold their exact matrix width (see
      // fixedColumnWidth) and the centred inner row puts the block mid-panel.
      if (this.fixedColumnWidth) return { flex: '0 0 auto' }

      return { flex: w + ' 1 0', minWidth: 0 }
    }
  },

  methods: {
    dividerAllowed (index) {
      return dividerAllowedFor(index, this.columnCount, this.hasPromo)
    },

    onFilterSearch () {
      if (this.inert) return

      this.$emit('filter-search')
    },

    onResize () {
      this.windowWidth = window.innerWidth
      this.windowHeight = window.innerHeight
      this.measureTop()
    },

    /** Where the panel starts on screen: the bottom of the header + nav strip. */
    measureTop () {
      this.panelTop = this.$el.getBoundingClientRect().top
    }
  }
}
</script>

<style scoped lang="scss">
.mm-panel {
  background-color: #ffffff;
  border-top: 1px solid #e3e2e2;
  border-bottom: 1px solid #e3e2e2;
  overflow: hidden;
  box-shadow: 0 12px 20px -12px rgba(0, 0, 0, 0.25);

  /* 0-column menus (promo only): panel sizes to its content up to its inline
     max-height. Flex-column layout propagates that bound down the tree so the
     tile grid — not the outer panel — owns the vertical scroll. Scrollbar
     therefore sits inside the promo pane next to the tiles rather than at the
     viewport edge. */
  &--auto {
    display: flex;
    flex-direction: column;
    height: auto;
  }
}

/* Propagate the panel's height cap to the promo pane via flex. Without these
   rules the inner + .mm-promo keep height:100% of an auto-height parent, which
   resolves to auto and leaves the grid's overflow-y:auto with nothing to
   scroll against. */
.mm-panel--auto > .mm-panel__inner {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
}

.mm-panel--auto ::v-deep .mm-promo {
  flex: 1 1 auto;
  min-height: 0;
}

/* 0-col menus must never show a scrollbar: effectivePaneHeight already shrinks
   the budget so tile math fits the cap, and panel overflow:hidden clips any
   residual sub-pixel overflow. Overriding the grid's default overflow-y:auto
   here is the belt + suspenders — client said no scroll even if content
   overshoots. */
.mm-panel--auto ::v-deep .mm-promo__grid {
  overflow-y: hidden;
}

/* Desktop (>=1280): panel is PINNED at 82% of the viewport height on every
   screen (client — "fix the height of the mega menu 82vh on all screens
   according to the viewport on desktop"). Content adapts within this bound:
   effectivePaneHeight pipes 0.82 * innerHeight into MegaMenuPromoPane so tiles
   are sized against the actual viewport, not the designed 580px pane. Flex
   column + bounded max-height lets .mm-panel__inner (flex: 1 1 auto) share
   that bound with .mm-panel__columns, .mm-column and .mm-promo so their
   `height: 100%` resolves correctly and their internal scrollbars
   (.mm-column__rows, .mm-promo__grid) own the overflow — never the viewport.
   Tablet (<=1279) rules above are untouched. */
@media (min-width: 1280px) {
  .mm-panel {
    display: flex;
    flex-direction: column;
    height: 82vh !important;
    max-height: 82vh !important;
    overflow: visible;
  }

  /* 0-col (promo-only) menus: auto-size to natural content, capped at 82vh.
     Small presets collapse the panel to its content; big presets hit the cap
     and the .mm-promo__grid internal overflow-y:auto owns the scroll. The
     flex chain (panel -> inner -> .mm-promo -> .mm-promo__grid, each with
     flex: 1 1 auto + min-height: 0) propagates the cap down so the grid
     bounds itself against 82vh and scrolls, instead of the panel growing. */
  .mm-panel--auto,
  .mm-panel--fit {
    height: auto !important;
    max-height: 82vh !important;
  }

  .mm-panel > .mm-panel__inner {
    flex: 1 1 auto;
    min-height: 0;
  }
}

/* Tablet range (768-1279). The panel height comes from an inline style (fill the
   viewport under the header, see tabletHeight), so no fixed height rules here.
   Menus whose columns and promo pane share a row size to their content up to
   that cap, as on desktop. Menus that wrap (3-4 columns, narrow) scroll as one
   unit: the inner scrolls instead of the columns and the pane each trapping a
   scrollbar. */
@media (min-width: 768px) and (max-width: 1279px) {
  .mm-panel--tablet-fit {
    display: flex;
    flex-direction: column;
    height: auto !important;
    overflow: visible;
  }

  .mm-panel--tablet-fit > .mm-panel__inner {
    flex: 1 1 auto;
    min-height: 0;
  }

  .mm-panel--auto {
    display: flex;
    flex-direction: column;
    height: auto !important;
  }

  .mm-panel--tablet-stacked {
    overflow: hidden;
  }

  .mm-panel__inner--wrap:not(.mm-panel__inner--tablet-fit) {
    height: 100% !important;
    max-height: 100% !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    scrollbar-width: thin;
  }
}

.mm-panel__inner {
  position: relative;
  display: flex;
  align-items: stretch;
  height: 100%;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  &--centered {
    justify-content: center;
  }
}

.mm-panel__columns {
  /* flex: 0 0 auto — columns hold their matrix widths exactly at desktop. The
     768-1279 media block relaxes this so cols wrap vertically inside a
     single-column-wide block. */
  position: relative;
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
}

/* Tablet range (768-1279): inner padding is trimmed so everything fits at the
   768px lower edge. Menus that share a row flex like desktop (columns and promo
   pane divide the row in their matrix ratio, see columnsStyle). Menus that wrap
   keep the columns on one full-width row, each column growing to share it, and
   send the promo pane to the next row via .mm-promo--tablet-stacked. */
@media (min-width: 768px) and (max-width: 1279px) {
  .mm-panel__inner {
    padding-left: 20px !important;
    padding-right: 20px !important;
    align-content: flex-start;
  }

  .mm-panel__inner--wrap:not(.mm-panel__inner--tablet-fit) {
    flex-wrap: wrap !important;
  }

  .mm-panel__inner:not(.mm-panel__inner--tablet-fit) .mm-panel__columns {
    flex: 0 0 auto !important;
    max-width: none !important;
    width: auto !important;
    min-width: 190px !important;
    flex-wrap: nowrap !important;
    align-items: stretch;
  }

  .mm-panel__inner--wrap:not(.mm-panel__inner--tablet-fit) .mm-panel__columns {
    flex: 1 1 100% !important;
    width: 100% !important;
    max-width: none !important;
    gap: 24px !important;
    min-height: 0;
  }

  .mm-panel__inner--wrap:not(.mm-panel__inner--tablet-fit) ::v-deep .mm-column {
    flex: 1 1 0 !important;
    width: auto !important;
    max-width: none !important;
    min-width: 0 !important;
  }
}

.mm-panel__filter {
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
}

/* 0-column + FILTER SEARCH: flex-column so the promo pane fills the middle and
   the filter button sits at the bottom, in flow. The tile grid (base rule
   overflow-y:auto + the flex chain from .mm-panel--auto above) owns the
   internal scroll, so the scrollbar hugs the tiles instead of the outer edge. */
.mm-panel__inner--promo-filter {
  flex-direction: column;
  align-items: stretch;
}

/* Filter button pinned to the bottom of the flex chain, in flow. */
.mm-panel--promo-filter .mm-panel__filter--promo {
  position: static;
  flex: 0 0 auto;
  padding-top: 12px;
  padding-bottom: 24px;
}

.mm-panel__filter-btn {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #383838;
  background: #ffffff;
  border: 1px solid #383838;
  padding: 10px 22px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: #383838;
    color: #ffffff;
  }
}

/* Upward-pointing triangle tail for 2-col + promo p2_2_stk menus.
   Left-aligned — centering under the active nav item would require
   JS and is fragile at varying viewports. */
.mm-panel__tail {
  position: absolute;
  top: 0;
  left: 30px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #ffffff;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: -11px;
    width: 0;
    height: 0;
    border-left: 11px solid transparent;
    border-right: 11px solid transparent;
    border-bottom: 11px solid #e3e2e2;
    z-index: -1;
  }
}

</style>
